import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import useBus from "use-bus";
import UpdateComponent from "@entities/UpdateComponent/UpdateComponent";
import styles from "./layout.module.scss";
import { appStore } from "@/app/store/appStore";
import { EventActionError } from "@/shared/bus/events";
import { getSupportedApp } from "@/widgets/StepsManager/utils/helpers";
import { Navigation } from "@/features/Navigation/Navigation";
import { ErrorBlock } from "@/entities/layout/ui/ErrorBlock/ErrorBlock";
import { Footer } from "@/entities/layout/ui/Footer/Footer";
import OpenApiClient from "@/api/OpenApiClient";
import { FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@/api/gen";
import { delay } from "@features/FxLauncherLoader/utils/helpers";
import MaintenanceComponent from "@entities/MaintenanceComponent/MaintenanceComponent";
import { em, LoadingOverlay, Modal } from "@mantine/core";
import { StepsContent } from "@features/StepsContent/StepsContent";
import { emailStepStore } from "@entities/StepEmail/store/emailStepStore";
import useAsyncEffect from "use-async-effect";
import { persistStore } from "@entities/StepFolder/store/persistStore";
import Sender, { AnalyticsSender } from "@/api/Analytics";
import { useMediaQuery } from "@mantine/hooks";

function GameSetup() {
	const appCodeUpdated = appStore((state) => state.appCodeUpdated);
	const refreshToken = persistStore((state) => state.refreshToken);
	const setAppCode = appStore((state) => state.setAppCode);

	const { game } = useParams<{ game: string }>();
	const [searchParams] = useSearchParams();

	const setupGame = useCallback(async () => {
		const newAppCode = getSupportedApp(game || searchParams.get("app") || null);

		let gameData = null;

		while (!gameData) {
			try {
				gameData = await OpenApiClient.PublicGameConfigApi.fxIdWebFeaturesPlayGamePublicConfigEndpoint(
					newAppCode,
					FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxLauncher
				);
			} catch (e) {
				console.log(e);
				await delay(2000);
			}
		}

		setAppCode(newAppCode, gameData);
		// Флаг для пререндера что можно обрабатывать страницу
		window.prerenderReady = true;
	}, [game, searchParams, setAppCode]);

	const isLoading = emailStepStore((state) => state.isLoading);
	const [loginFlow, fxApiSdkCache, appCode] = appStore((state) => [
		state.loginFlow,
		state.fxApiSdkCache,
		state.appCode
	]);

	useEffect(() => {
		setupGame();
	}, [game, searchParams, setAppCode, setupGame]);

	const setFxApiSdkCache = appStore.getState().setFxApiSdkCache;

	useAsyncEffect(async () => {
		if (!appCodeUpdated) {
			return;
		}
		const clientGuid = persistStore.getState().clientGuid;
		let appVersion = 0;
		let hwProfileGuid = "-1";
		let loaderName = "-1";
		let apiType = "-1";
		let cefVersion = "-1";
		let steamIsDeck = -1;

		try {
			if (typeof FxApi !== "undefined" && FxApi.getAppVersion()) {
				try {
					appVersion = FxApi.getAppVersion();
				} catch (e) {
					console.log("error getting app version", e);
				}
				try {
					hwProfileGuid = await FxApi.getHwProfileGuid();
				} catch (e) {
					console.log("error getting getHwProfileGuid", e);
				}

				try {
					loaderName = await FxApi.getLoaderName();

					function extractIdFromFilename(filename: string): string {
						const regex = /__([a-zA-Z0-9]+)/;
						const match = filename.match(regex);
						return match ? match[1] : "";
					}

					AnalyticsSender.SessionId = extractIdFromFilename(loaderName);
				} catch (e) {
					console.log("error getting getLoaderName", e);
				}

				try {
					apiType = FxApi.getApiType();
				} catch (e) {
					console.log("error getting getApiType", e);
				}
				try {
					cefVersion = FxApi.getCefVersion();
				} catch (e) {
					console.log("error getting getCefVersion", e);
				}

				try {
					steamIsDeck = (await FxApi.Steam.isDeck()).deck ? 1 : 0;
				} catch (e) {
					console.log("error getting steamIsDeck", e);
				}
			}
		} catch (e) {
			console.log("error getting fxapi state", e);
		}
		const sdkState = {
			appCode,
			clientGuid,
			apiType,
			cefVersion,
			appVersion,
			hwProfileGuid,
			loaderName,
			steamIsDeck
		};
		setFxApiSdkCache(sdkState);
	}, [appCodeUpdated]);

	useEffect(() => {
		if (fxApiSdkCache) {
			Sender.SendLauncherStarted(
				fxApiSdkCache.appCode,
				fxApiSdkCache.clientGuid,
				fxApiSdkCache.apiType,
				fxApiSdkCache.cefVersion,
				fxApiSdkCache.appVersion.toString(),
				fxApiSdkCache.loaderName,
				fxApiSdkCache.hwProfileGuid,
				fxApiSdkCache.steamIsDeck
			);
		}
	}, [fxApiSdkCache, refreshToken]);

	const { setIsLoading, startRequest, setExternalLoginState } = emailStepStore.getState();

	function removeHash() {
		history.pushState("", document.title, window.location.pathname + window.location.search);
	}

	const location = useLocation();
	useEffect(() => {
		if (!appCodeUpdated) return;
		try {
			const urlParams = new URLSearchParams(location.hash.slice(1));
			const refreshToken = urlParams.get("fxid-auth-token");
			console.log(`!!! got refreshToken ${refreshToken}`);
			if (refreshToken) {
				removeHash();
				startRequest(refreshToken);
				persistStore.getState().setRefreshToken(refreshToken);
			}
		} catch (error) {
			console.log(`Error parsing hash: ${error}`);
		}
	}, [appCodeUpdated, location, setExternalLoginState, setIsLoading, startRequest]);

	const isPopup = useMediaQuery(`(max-width: ${em(520)})`);

	useEffect(() => {
        const minWidth = 500;
        const minHeight = 560;
        let resizeTimeout: NodeJS.Timeout | null = null;
        
        const handleResize = () => {
            // Clear the existing timeout if there is one
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            // Set a new timeout to execute the resize logic after 300ms of inactivity
            resizeTimeout = setTimeout(() => {
                if (window.innerWidth < minWidth || window.innerHeight < minHeight) {
                    window.resizeTo(
                        Math.max(minWidth, window.innerWidth),
                        Math.max(minHeight, window.innerHeight)
                    );
                }
            }, 300); // 300ms delay
        };

        // Initial check
        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            // Clear any pending timeout when the component unmounts
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);

	if (!appCodeUpdated) return null;

	// TODO: Тут можно поставить ReactHelm что бы рендерить правильные меты, как только они отрендерились можно поставить window.prerenderReady = true;
	return (
		appCodeUpdated && (
			<>
				{!isPopup && (
					<Modal.Root
						padding={1}
						centered={true}
						closeOnClickOutside={false}
						size={600}
						opened={loginFlow}
						onClose={() => {
							appStore.getState().setLoginFlow(false);
						}}
					>
						<Modal.Overlay zIndex={0} />
						<Modal.Content>
							<Modal.Body style={{ zIndex: 0 }}>
								<LoadingOverlay
									visible={isLoading}
									zIndex={1000}
									overlayProps={{ radius: "sm", blur: 2 }}
								/>
								<StepsContent />
							</Modal.Body>
						</Modal.Content>
					</Modal.Root>
				)}
				{loginFlow && isPopup ? <StepsContent /> : <Outlet></Outlet>}
			</>
		)
	);
}

export default GameSetup;
